import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AddStudent from "./AddStudent";

const StudentDetail = () => {
  const token = localStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notePage, setNotePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteTopic, setNoteTopic] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("notes");
  // Files tab state
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [fileToUpload, setFileToUpload] = useState(null);
  const [filePurpose, setFilePurpose] = useState("");
  const [showAddStudent, setShowAddStudent] = useState(false);
  // Modal state for add note
  const [showAddNote, setShowAddNote] = useState(false);
  // State for share with in add note popup (multi-select)
  const [shareWithAddNote, setShareWithAddNote] = useState([]);
  const [shareAllAddNote, setShareAllAddNote] = useState(false);
  const [shareBoxOpen, setShareBoxOpen] = useState(false);
  const [shareQuery, setShareQuery] = useState("");
  // State for users in same school
  const [users, setUsers] = useState([]);
  // Modal for sharing existing note
  const [shareModal, setShareModal] = useState({
    open: false,
    noteId: null,
    email: "",
  });
  // Toast notification state
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const [userProfile, setUserProfile] = useState(null);
  const [editStudentOpen, setEditStudentOpen] = useState(false);
  const [editStudentForm, setEditStudentForm] = useState({
    firstName: "",
    lastName: "",
    grade: "",
    referredBy: "",
    referredReason: "",
    studentSupportStatus: [],
  });
  const [supportEditOpen, setSupportEditOpen] = useState(false);
  const supportEditRef = useRef(null);
  // Meetings tab state
  const [meetingDate, setMeetingDate] = useState(""); // YYYY-MM-DD
  const [meetingBusy, setMeetingBusy] = useState(false);
  const [meetingError, setMeetingError] = useState("");
  useEffect(() => {
    const handler = (e) => {
      if (!supportEditRef.current) return;
      if (!supportEditRef.current.contains(e.target)) setSupportEditOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const showToast = (message) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  // Fetch profile for header controls (role, logout)
  useEffect(() => {
    async function fetchProfile() {
      try {
        if (!token) return;
        const res = await fetch(`${apiUrl.replace(/\/$/, "")}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch user profile");
        const data = await res.json();
        setUserProfile(data);
      } catch {
        setUserProfile(null);
      }
    }
    fetchProfile();
  }, [apiUrl, token]);
  // eslint-disable-next-line no-unused-vars
  const handleLogout = async () => {
    try {
      await fetch(`${apiUrl.replace(/\/$/, "")}/logout`, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
    } catch {}
    localStorage.removeItem("token");
    navigate("/login");
  };
  // Fetch users in same school (callable)
  const fetchUsers = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  // Edit Note modal state
  const [editModal, setEditModal] = useState({
    open: false,
    noteId: null,
    content: "",
    topic: "",
  });

  // View Note modal state (read-only viewer)
  const [viewModal, setViewModal] = useState({
    open: false,
    note: null,
  });
  // Ref for auto-growing edit textarea
  const editContentRef = useRef(null);

  // Fetch student info and notes
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError("");
      try {
        // Get student info
        const resStudent = await fetch(
          `${apiUrl.replace(/\/$/, "")}/students`,
          {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
        const dataStudent = await resStudent.json();
        const found = (dataStudent.students || []).find(
          (s) => s.studentId === studentId
        );
        setStudent(found);
        // Get notes for this student
        const resNotes = await fetch(`${apiUrl.replace(/\/$/, "")}/notes`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        const dataNotes = await resNotes.json();
        // Filter and sort notes for this student
        const filtered = (dataNotes.notes || []).filter(
          (n) => n.studentId === studentId
        );
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setNotes(filtered);
        setNotePage(1);
        // Auto-open a note if requested via query param (?openNote=...)
        try {
          const params = new URLSearchParams(window.location.search);
          const openNoteId = params.get("openNote");
          if (openNoteId) {
            const target = filtered.find((n) => n.noteId === openNoteId);
            if (target) {
              setViewModal({ open: true, note: target });
              // Optionally fetch the note by ID to trigger server-side VIEWED audit
              const token = localStorage.getItem("token");
              fetch(`${apiUrl.replace(/\/$/, "")}/notes/${openNoteId}`, {
                headers: { Authorization: token ? `Bearer ${token}` : "" },
              }).catch(() => {});
              // Clean the query param so it doesn't auto-open again on refresh
              try {
                navigate(`/students/${studentId}`, { replace: true });
              } catch {}
            }
          }
        } catch {}
      } catch (err) {
        setError("Failed to load student info or notes");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, [studentId]);

  // Fetch files for this student when switching to Files tab
  useEffect(() => {
    const fetchFiles = async () => {
      if (tab !== "files") return;
      setFilesLoading(true);
      try {
        const res = await fetch(
          `${apiUrl.replace(/\/$/, "")}/students/${studentId}/files`,
          {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load files");
        setFiles(Array.isArray(data.files) ? data.files : []);
      } catch (e) {
        console.error(e);
        setFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };
    fetchFiles();
  }, [tab, apiUrl, token, studentId]);

  // When the view modal opens, mark the note as read locally (to clear "Unread" badges on dashboard)
  useEffect(() => {
    if (viewModal?.open && viewModal?.note?.noteId && userProfile?.email) {
      try {
        localStorage.setItem(
          `noteRead:${userProfile.email}:${viewModal.note.noteId}`,
          "1"
        );
      } catch {}
    }
  }, [viewModal, userProfile]);

  // Add note
  const handleAddNote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify({
          content: noteContent,
          topic: noteTopic,
          studentId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add note");
      setNoteContent("");
      setNoteTopic("");
      setNotes((prev) => [data.note, ...prev]);
      setNotePage(1);
      return data.note;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  // Pagination: 5 notes per page
  const NOTES_PER_PAGE = 5;
  const totalNotePages = Math.max(
    1,
    Math.ceil((notes?.length || 0) / NOTES_PER_PAGE)
  );
  const startIdx = (notePage - 1) * NOTES_PER_PAGE;
  const paginatedNotes = notes.slice(startIdx, startIdx + NOTES_PER_PAGE);

  // Clamp page if notes shrink
  useEffect(() => {
    const newTotal = Math.max(
      1,
      Math.ceil((notes?.length || 0) / NOTES_PER_PAGE)
    );
    if (notePage > newTotal) setNotePage(newTotal);
  }, [notes, notePage]);

  // Auto-grow the edit note textarea up to a max height
  useEffect(() => {
    const el = editContentRef.current;
    if (!el || !editModal.open) return;
    const adjust = () => {
      const maxPx = Math.floor(window.innerHeight * 0.5); // up to ~50% viewport height
      el.style.height = "auto";
      el.style.maxHeight = `${maxPx}px`;
      el.style.overflowY = "auto";
      el.style.height = `${Math.min(el.scrollHeight, maxPx)}px`;
    };
    // Adjust on open and when content changes
    requestAnimationFrame(adjust);
  }, [editModal.open, editModal.content]);

  if (loading) return <div style={{ padding: 32 }}>Loading...</div>;
  if (error) return <div style={{ color: "red", padding: 32 }}>{error}</div>;
  if (!student) return <div style={{ padding: 32 }}>Student not found.</div>;

  // Header/nav copied from Dashboard
  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh" }}>
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#10b981",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            zIndex: 1000,
            fontWeight: 600,
          }}
          role="status"
          aria-live="polite"
        >
          {toast}
        </div>
      )}
      {/* Add Student Modal */}
      {showAddStudent && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowAddStudent(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 4px 32px rgba(0,0,0,0.13)",
              padding: 24,
              minWidth: 340,
              maxWidth: 520,
              width: "90vw",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAddStudent(false)}
              style={{
                position: "absolute",
                top: 12,
                right: 16,
                background: "none",
                border: "none",
                fontSize: 22,
                color: "#64748b",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              ×
            </button>
            <AddStudent onStudentAdded={() => setShowAddStudent(false)} />
          </div>
        </div>
      )}

      <div style={{ maxWidth: 700, margin: "0 auto", padding: 32 }}>
        <button
          onClick={() => navigate("/Students")}
          aria-label="Back to Home"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "#0f172a",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 9999,
            padding: "8px 12px",
            marginBottom: 16,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          <span style={{ fontSize: 16 }}>←</span>
          <span style={{ fontSize: 14 }}>Back</span>
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <h1 style={{ margin: 0 }}>
            {student.firstName} {student.lastName}{" "}
            <span style={{ color: "#64748b", fontSize: 18 }}>
              (Grade {student.grade})
            </span>
          </h1>
          <button
            onClick={() => {
              setEditStudentForm({
                firstName: student.firstName || "",
                lastName: student.lastName || "",
                grade: student.grade || "",
                referredBy: student.referredBy || "",
                referredReason: student.referredReason || "",
                studentSupportStatus: Array.isArray(
                  student.studentSupportStatus
                )
                  ? student.studentSupportStatus
                  : student.studentSupportStatus
                  ? [student.studentSupportStatus]
                  : [],
              });
              setEditStudentOpen(true);
            }}
            style={{
              padding: "6px 12px",
              fontSize: 14,
              background: "#e0e7ff",
              color: "#3730a3",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              marginLeft: "auto",
            }}
          >
            Edit Student
          </button>
        </div>
        <div
          style={{
            margin: "16px 0 32px 0",
            color: "#555",
            display: "grid",
            gap: 6,
          }}
        >
          <div>
            <strong>Referred By:</strong> {student.referredBy || "-"}
          </div>
          <div>
            <strong>Referred Reason:</strong> {student.referredReason || "-"}
          </div>
          <div>
            <strong>Support Status:</strong>{" "}
            {Array.isArray(student.studentSupportStatus)
              ? student.studentSupportStatus.length
                ? student.studentSupportStatus.join(", ")
                : "-"
              : student.studentSupportStatus || "-"}
          </div>
        </div>

        {/* Removed 'Share most recent note' UI per request */}

        {/* Tabs for Notes, Files, and Recent Meetings */}
        <div
          style={{
            display: "flex",
            gap: 24,
            fontSize: 17,
            fontWeight: 500,
            marginBottom: 18,
          }}
        >
          <span
            style={{
              borderBottom: tab === "notes" ? "3px solid #2563eb" : "none",
              color: tab === "notes" ? "#2563eb" : "#222",
              paddingBottom: 6,
              cursor: "pointer",
            }}
            onClick={() => setTab("notes")}
          >
            Notes
          </span>
          <span
            style={{
              borderBottom: tab === "files" ? "3px solid #2563eb" : "none",
              color: tab === "files" ? "#2563eb" : "#222",
              paddingBottom: 6,
              cursor: "pointer",
            }}
            onClick={() => setTab("files")}
          >
            Files
          </span>
          <span
            style={{
              borderBottom: tab === "meetings" ? "3px solid #2563eb" : "none",
              color: tab === "meetings" ? "#2563eb" : "#222",
              paddingBottom: 6,
              cursor: "pointer",
            }}
            onClick={() => setTab("meetings")}
          >
            Recent Meetings
          </span>
        </div>

        {tab === "notes" && (
          <>
            {/* Edit Student Modal */}
            {editStudentOpen && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.25)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setEditStudentOpen(false)}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 4px 32px rgba(0,0,0,0.13)",
                    padding: 28,
                    minWidth: 320,
                    maxWidth: 520,
                    width: "90vw",
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setEditStudentOpen(false)}
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 16,
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                  <h2 style={{ marginTop: 0, marginBottom: 16 }}>
                    Edit Student
                  </h2>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setSubmitting(true);
                      setError("");
                      try {
                        const res = await fetch(
                          `${apiUrl.replace(/\/$/, "")}/students/${studentId}`,
                          {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                            },
                            body: JSON.stringify(editStudentForm),
                          }
                        );
                        const data = await res.json();
                        if (!res.ok)
                          throw new Error(
                            data.error || "Failed to update student"
                          );
                        setStudent((prev) => ({ ...prev, ...data.student }));
                        setEditStudentOpen(false);
                        showToast("Student updated!");
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    <div style={{ display: "grid", gap: 10 }}>
                      <label
                        htmlFor="edit-first-name"
                        style={{ fontSize: 12, color: "#334155" }}
                      >
                        First Name
                      </label>
                      <input
                        id="edit-first-name"
                        type="text"
                        placeholder="First Name"
                        value={editStudentForm.firstName}
                        onChange={(e) =>
                          setEditStudentForm((f) => ({
                            ...f,
                            firstName: e.target.value,
                          }))
                        }
                        required
                        style={{
                          padding: 10,
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                        }}
                      />
                      <label
                        htmlFor="edit-last-name"
                        style={{ fontSize: 12, color: "#334155" }}
                      >
                        Last Name
                      </label>
                      <input
                        id="edit-last-name"
                        type="text"
                        placeholder="Last Name"
                        value={editStudentForm.lastName}
                        onChange={(e) =>
                          setEditStudentForm((f) => ({
                            ...f,
                            lastName: e.target.value,
                          }))
                        }
                        required
                        style={{
                          padding: 10,
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                        }}
                      />
                      <label
                        htmlFor="edit-grade"
                        style={{ fontSize: 12, color: "#334155" }}
                      >
                        Grade
                      </label>
                      <input
                        id="edit-grade"
                        type="text"
                        placeholder="Grade"
                        value={editStudentForm.grade}
                        onChange={(e) =>
                          setEditStudentForm((f) => ({
                            ...f,
                            grade: e.target.value,
                          }))
                        }
                        required
                        style={{
                          padding: 10,
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                        }}
                      />
                      <label
                        htmlFor="edit-referred-by"
                        style={{ fontSize: 12, color: "#334155" }}
                      >
                        Referred By
                      </label>
                      <input
                        id="edit-referred-by"
                        type="text"
                        placeholder="Referred By"
                        value={editStudentForm.referredBy}
                        onChange={(e) =>
                          setEditStudentForm((f) => ({
                            ...f,
                            referredBy: e.target.value,
                          }))
                        }
                        style={{
                          padding: 10,
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                        }}
                      />
                      <label
                        htmlFor="edit-referred-reason"
                        style={{ fontSize: 12, color: "#334155" }}
                      >
                        Referred Reason
                      </label>
                      <select
                        id="edit-referred-reason"
                        value={editStudentForm.referredReason}
                        onChange={(e) =>
                          setEditStudentForm((f) => ({
                            ...f,
                            referredReason: e.target.value,
                          }))
                        }
                        style={{
                          padding: 10,
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          background: "#fff",
                        }}
                      >
                        <option value="">-- Select Reason (optional) --</option>
                        <option value="Academic">Academic</option>
                        <option value="Behavioral">Behavioral</option>
                        <option value="Social/Emotional">
                          Social/Emotional
                        </option>
                        <option value="Family-related">Family-related</option>
                        <option value="Attendance">Attendance</option>
                      </select>
                      <label style={{ fontSize: 12, color: "#334155" }}>
                        Student Support Status (optional)
                      </label>
                      <div
                        style={{ position: "relative" }}
                        ref={supportEditRef}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setSupportEditOpen((o) => !o)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSupportEditOpen((o) => !o);
                            }
                          }}
                          style={{
                            padding: 10,
                            border: "1px solid #cbd5e1",
                            borderRadius: 6,
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                          }}
                        >
                          <span
                            style={{
                              color: (
                                editStudentForm.studentSupportStatus || []
                              ).length
                                ? "#0f172a"
                                : "#64748b",
                            }}
                          >
                            {(editStudentForm.studentSupportStatus || [])
                              .length === 0
                              ? "Select support status"
                              : editStudentForm.studentSupportStatus.join(", ")}
                          </span>
                          <span style={{ color: "#475569" }}>▾</span>
                        </div>
                        {supportEditOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              background: "#fff",
                              border: "1px solid #cbd5e1",
                              borderRadius: 6,
                              boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
                              marginTop: 4,
                              padding: 8,
                              zIndex: 20,
                            }}
                          >
                            {[
                              "IEP",
                              "504",
                              "RTI Tier 1",
                              "RTI Tier 2",
                              "RTI Tier 3",
                              "Counseling only",
                            ].map((opt) => {
                              const checked = (
                                editStudentForm.studentSupportStatus || []
                              ).includes(opt);
                              return (
                                <label
                                  key={opt}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "4px 6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setEditStudentForm((prev) => {
                                        const set = new Set(
                                          prev.studentSupportStatus || []
                                        );
                                        if (isChecked) set.add(opt);
                                        else set.delete(opt);
                                        return {
                                          ...prev,
                                          studentSupportStatus: Array.from(set),
                                        };
                                      });
                                    }}
                                  />
                                  <span>{opt}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button
                        type="submit"
                        disabled={submitting}
                        style={{
                          padding: "10px 16px",
                          background: "#2563eb",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {submitting ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditStudentOpen(false)}
                        style={{
                          padding: "10px 16px",
                          background: "#e5e7eb",
                          color: "#111",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {error && (
                      <div style={{ color: "#b91c1c", marginTop: 10 }}>
                        {error}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}
            {/* Add Note Popup Trigger */}
            <button
              onClick={() => setShowAddNote(true)}
              style={{
                marginBottom: 24,
                padding: "10px 24px",
                fontSize: 16,
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Add Note
            </button>

            {/* Add Note Modal Popup */}
            {showAddNote && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.25)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setShowAddNote(false)}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 4px 32px rgba(0,0,0,0.13)",
                    padding: 32,
                    minWidth: 340,
                    maxWidth: 480,
                    width: "90vw",
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setShowAddNote(false)}
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 16,
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                  <h2 style={{ marginTop: 0, marginBottom: 18 }}>Add Note</h2>
                  <form
                    onSubmit={async (e) => {
                      const created = await handleAddNote(e);
                      if (!created) return;
                      const hasRecipients =
                        shareAllAddNote ||
                        (shareWithAddNote && shareWithAddNote.length > 0);
                      if (!hasRecipients) {
                        setShowAddNote(false);
                        return;
                      }
                      setSubmitting(true);
                      setError("");
                      try {
                        const recipients = shareAllAddNote
                          ? (users || [])
                              .filter(
                                (u) =>
                                  !userProfile?.email ||
                                  u.email !== userProfile.email
                              )
                              .map((u) => u.email)
                          : shareWithAddNote;
                        if (!recipients || recipients.length === 0) {
                          setShowAddNote(false);
                          return;
                        }
                        const res = await fetch(
                          `${apiUrl.replace(/\/$/, "")}/notes/${
                            created.noteId
                          }/share`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                            },
                            body: JSON.stringify({
                              shareWith: recipients,
                              consent: true,
                              consentMethod: "manual",
                            }),
                          }
                        );
                        const data = await res.json();
                        if (!res.ok)
                          throw new Error(data.error || "Failed to share note");
                        showToast("Note added and shared!");
                        setShowAddNote(false);
                        setShareBoxOpen(false);
                        setShareWithAddNote([]);
                        setShareAllAddNote(false);
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    <input
                      type="text"
                      value={noteTopic}
                      onChange={(e) => setNoteTopic(e.target.value)}
                      placeholder="Topic (e.g., Academics, Wellbeing, etc.)"
                      style={{
                        width: "100%",
                        padding: 10,
                        fontSize: 15,
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        marginBottom: 10,
                      }}
                    />
                    <textarea
                      value={noteContent}
                      onChange={(e) => {
                        setNoteContent(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                      }}
                      placeholder="Add a new note..."
                      rows={1}
                      style={{
                        width: "100%",
                        padding: 12,
                        fontSize: 16,
                        fontFamily: "inherit",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        resize: "none",
                        overflow: "hidden",
                        minHeight: 60,
                      }}
                      required
                    />
                    <div
                      style={{
                        marginTop: 14,
                        display: "grid",
                        gridTemplateColumns: "auto minmax(200px, 1fr)",
                        gap: 12,
                        alignItems: "end",
                      }}
                    >
                      <button
                        type="submit"
                        disabled={submitting || !noteContent.trim()}
                        style={{
                          padding: "10px 24px",
                          fontSize: 16,
                          background: "#2563eb",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontWeight: 500,
                          alignSelf: "end",
                        }}
                      >
                        {submitting ? "Adding..." : "Add Note"}
                      </button>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          position: "relative",
                          maxWidth: 340,
                          alignSelf: "end",
                        }}
                      >
                        <label style={{ fontSize: 12, color: "#334155" }}>
                          Share with
                        </label>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={async () => {
                            await fetchUsers();
                            setShareBoxOpen((o) => !o);
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              await fetchUsers();
                              setShareBoxOpen((o) => !o);
                            }
                          }}
                          style={{
                            width: "100%",
                            maxWidth: 340,
                            boxSizing: "border-box",
                            padding: "8px 10px",
                            fontSize: 14,
                            borderRadius: 4,
                            border: "1px solid #cbd5e1",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                          }}
                        >
                          <span
                            style={{
                              color:
                                shareAllAddNote || shareWithAddNote.length
                                  ? "#0f172a"
                                  : "#64748b",
                            }}
                          >
                            {shareAllAddNote
                              ? "Everyone in my school"
                              : shareWithAddNote.length === 0
                              ? "Select recipients"
                              : shareWithAddNote.length === 1
                              ? (() => {
                                  const u = users.find(
                                    (x) => x.email === shareWithAddNote[0]
                                  );
                                  if (!u) return shareWithAddNote[0];
                                  return u.firstName && u.lastName
                                    ? `${u.firstName} ${u.lastName}`
                                    : u.email;
                                })()
                              : `${shareWithAddNote.length} selected`}
                          </span>
                          <span style={{ color: "#475569" }}>▾</span>
                        </div>
                        {shareBoxOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              background: "#fff",
                              border: "1px solid #cbd5e1",
                              borderRadius: 6,
                              boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
                              marginTop: 4,
                              padding: 8,
                              zIndex: 1100,
                              maxHeight: "60vh",
                              overflow: "auto",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Search people..."
                              value={shareQuery}
                              onChange={(e) => setShareQuery(e.target.value)}
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                fontSize: 13,
                                borderRadius: 4,
                                border: "1px solid #cbd5e1",
                                marginBottom: 6,
                              }}
                            />
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 13,
                                color: "#0f172a",
                                userSelect: "none",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={shareAllAddNote}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setShareAllAddNote(checked);
                                  if (checked) {
                                    const all = (users || [])
                                      .filter(
                                        (u) =>
                                          !userProfile?.email ||
                                          u.email !== userProfile.email
                                      )
                                      .map((u) => u.email);
                                    setShareWithAddNote(all);
                                  }
                                }}
                              />
                              Select all from my school
                            </label>
                            <div
                              style={{
                                maxHeight: 160,
                                overflow: "auto",
                                borderTop: "1px solid #e2e8f0",
                                borderBottom: "1px solid #e2e8f0",
                                marginTop: 6,
                                padding: "4px 2px",
                              }}
                            >
                              {users
                                .filter(
                                  (u) =>
                                    !userProfile?.email ||
                                    u.email !== userProfile.email
                                )
                                .filter((u) => {
                                  const q = shareQuery.trim().toLowerCase();
                                  if (!q) return true;
                                  const name = `${u.firstName || ""} ${
                                    u.lastName || ""
                                  }`.toLowerCase();
                                  return (
                                    name.includes(q) ||
                                    (u.email || "").toLowerCase().includes(q) ||
                                    (u.role || "").toLowerCase().includes(q)
                                  );
                                })
                                .map((u) => {
                                  const label =
                                    u.firstName && u.lastName
                                      ? `${u.firstName} ${u.lastName}`
                                      : u.email;
                                  const checked =
                                    shareAllAddNote ||
                                    shareWithAddNote.includes(u.email);
                                  return (
                                    <label
                                      key={u.email}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        padding: "4px 6px",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={shareAllAddNote}
                                        onChange={(e) => {
                                          const isChecked = e.target.checked;
                                          setShareAllAddNote(false);
                                          setShareWithAddNote((prev) => {
                                            if (isChecked)
                                              return Array.from(
                                                new Set([...prev, u.email])
                                              );
                                            return prev.filter(
                                              (em) => em !== u.email
                                            );
                                          });
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: 13,
                                          color: "#0f172a",
                                        }}
                                      >
                                        {label}
                                        {u.role ? ` (${u.role})` : ""}
                                      </span>
                                    </label>
                                  );
                                })}
                              {users.filter(
                                (u) =>
                                  !userProfile?.email ||
                                  u.email !== userProfile.email
                              ).length === 0 && (
                                <div
                                  style={{
                                    color: "#64748b",
                                    fontSize: 13,
                                    padding: 8,
                                  }}
                                >
                                  No users found.
                                </div>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: 6,
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setShareAllAddNote(false);
                                  setShareWithAddNote([]);
                                  setShareQuery("");
                                }}
                                style={{
                                  padding: "6px 10px",
                                  background: "#f1f5f9",
                                  color: "#0f172a",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontSize: 13,
                                }}
                              >
                                Clear
                              </button>
                              <button
                                type="button"
                                onClick={() => setShareBoxOpen(false)}
                                style={{
                                  padding: "6px 10px",
                                  background: "#2563eb",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontSize: 13,
                                }}
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                  {error && (
                    <div style={{ color: "#b91c1c", marginTop: 10 }}>
                      {error}
                    </div>
                  )}
                </div>
              </div>
            )}
            <h2 style={{ marginBottom: 12 }}>Notes</h2>
            {notes.length === 0 ? (
              <div style={{ color: "#888", fontSize: 16 }}>
                No notes for this student yet.
              </div>
            ) : (
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0 auto",
                  maxWidth: "700px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {paginatedNotes.map((note) => (
                  <li
                    key={note.noteId}
                    style={{
                      background: "#f8fafc",
                      borderRadius: 8,
                      marginBottom: 16,
                      padding: 16,
                      position: "relative",
                      width: "100%",
                      maxWidth: "680px",
                      wordBreak: "break-word",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                    }}
                    onClick={() => setViewModal({ open: true, note })}
                  >
                    {/* Inline editor removed; using modal editor instead */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 15,
                            color: "#111",
                            fontWeight: 600,
                          }}
                        >
                          {note.topic && note.topic.trim()
                            ? note.topic
                            : "No topic"}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "#64748b",
                            marginTop: 6,
                          }}
                        >
                          {new Date(note.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginLeft: 16,
                          alignItems: "center",
                          flexShrink: 0,
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                          maxWidth: 220,
                        }}
                      >
                        <button
                          style={{
                            padding: "4px 12px",
                            fontSize: 14,
                            background: "#e0e7ff",
                            color: "#3730a3",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditModal({
                              open: true,
                              noteId: note.noteId,
                              content: note.content || "",
                              topic: note.topic || "",
                            });
                          }}
                        >
                          Edit
                        </button>
                        <button
                          style={{
                            padding: "4px 12px",
                            fontSize: 14,
                            background: "#dbeafe",
                            color: "#0369a1",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await fetchUsers();
                            setShareModal({
                              open: true,
                              noteId: note.noteId,
                              email: "",
                            });
                          }}
                        >
                          Share With
                        </button>
                        {/* Share With Modal for existing notes */}
                        {shareModal.open && (
                          <div
                            style={{
                              position: "fixed",
                              top: 0,
                              left: 0,
                              width: "100vw",
                              height: "100vh",
                              background: "rgba(0,0,0,0.25)",
                              zIndex: 1000,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() =>
                              setShareModal({
                                open: false,
                                noteId: null,
                                email: "",
                              })
                            }
                          >
                            <div
                              style={{
                                background: "#fff",
                                borderRadius: 10,
                                boxShadow: "0 4px 32px rgba(0,0,0,0.13)",
                                padding: 32,
                                minWidth: 340,
                                maxWidth: 480,
                                width: "90vw",
                                position: "relative",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() =>
                                  setShareModal({
                                    open: false,
                                    noteId: null,
                                    email: "",
                                  })
                                }
                                style={{
                                  position: "absolute",
                                  top: 12,
                                  right: 16,
                                  background: "none",
                                  border: "none",
                                  fontSize: 22,
                                  color: "#64748b",
                                  cursor: "pointer",
                                }}
                                aria-label="Close"
                              >
                                ×
                              </button>
                              <h2 style={{ marginTop: 0, marginBottom: 18 }}>
                                Share Note
                              </h2>
                              <select
                                value={shareModal.email}
                                onChange={(e) =>
                                  setShareModal((sm) => ({
                                    ...sm,
                                    email: e.target.value,
                                  }))
                                }
                                style={{
                                  width: "100%",
                                  padding: 10,
                                  fontSize: 15,
                                  borderRadius: 4,
                                  border: "1px solid #ccc",
                                  marginBottom: 18,
                                }}
                                onFocus={fetchUsers}
                              >
                                <option value="">-- Share with --</option>
                                {users.map((u) => (
                                  <option key={u.email} value={u.email}>
                                    {u.firstName && u.lastName
                                      ? `${u.firstName} ${u.lastName}`
                                      : u.email}
                                    {u.role ? ` (${u.role})` : ""}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                disabled={!shareModal.email}
                                style={{
                                  padding: "10px 24px",
                                  fontSize: 16,
                                  background: "#0ea5e9",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontWeight: 500,
                                }}
                                onClick={async () => {
                                  if (!shareModal.noteId || !shareModal.email)
                                    return;
                                  setSubmitting(true);
                                  setError("");
                                  try {
                                    const res = await fetch(
                                      `${apiUrl.replace(/\/$/, "")}/notes/${
                                        shareModal.noteId
                                      }/share`,
                                      {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                          Authorization: token
                                            ? `Bearer ${token}`
                                            : undefined,
                                        },
                                        body: JSON.stringify({
                                          shareWith: [shareModal.email],
                                          consent: true,
                                          consentMethod: "manual",
                                        }),
                                      }
                                    );
                                    const data = await res.json();
                                    if (!res.ok)
                                      throw new Error(
                                        data.error || "Failed to share note"
                                      );
                                    showToast("Note shared!");
                                    setShareModal({
                                      open: false,
                                      noteId: null,
                                      email: "",
                                    });
                                  } catch (err) {
                                    setError(err.message);
                                  } finally {
                                    setSubmitting(false);
                                  }
                                }}
                              >
                                Share
                              </button>
                              {error && (
                                <div
                                  style={{ color: "#b91c1c", marginTop: 10 }}
                                >
                                  {error}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Notes Pagination */}
            {notes.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <button
                  onClick={() => setNotePage((p) => Math.max(1, p - 1))}
                  disabled={notePage === 1}
                  style={{
                    padding: "6px 12px",
                    fontSize: 14,
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    background: notePage === 1 ? "#e5e7eb" : "#fff",
                    color: "#111",
                    cursor: notePage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Prev
                </button>
                <span style={{ color: "#475569", fontSize: 14 }}>
                  Page {notePage} of {totalNotePages}
                </span>
                <button
                  onClick={() =>
                    setNotePage((p) => Math.min(totalNotePages, p + 1))
                  }
                  disabled={notePage === totalNotePages}
                  style={{
                    padding: "6px 12px",
                    fontSize: 14,
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    background:
                      notePage === totalNotePages ? "#e5e7eb" : "#fff",
                    color: "#111",
                    cursor:
                      notePage === totalNotePages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {/* View Note Modal (read-only) */}
            {viewModal.open && viewModal.note && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.25)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setViewModal({ open: false, note: null })}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 4px 32px rgba(0,0,0,0.13)",
                    padding: 28,
                    minWidth: 360,
                    maxWidth: 640,
                    width: "92vw",
                    maxHeight: "80vh",
                    overflow: "auto",
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setViewModal({ open: false, note: null })}
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 16,
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                  <h2 style={{ margin: 0, marginBottom: 8 }}>Note Details</h2>
                  <div
                    style={{
                      color: "#0f172a",
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {viewModal.note.topic && viewModal.note.topic.trim()
                      ? viewModal.note.topic
                      : "No topic"}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      marginTop: 2,
                      lineHeight: 1.3,
                    }}
                  >
                    {new Date(viewModal.note.createdAt).toLocaleString()}
                  </div>
                  <hr
                    style={{
                      border: 0,
                      borderTop: "1px solid #e2e8f0",
                      margin: "10px 0",
                    }}
                  />
                  <div
                    style={{
                      whiteSpace: "pre-line",
                      lineHeight: 1.4,
                      color: "#111827",
                      fontSize: 15,
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxHeight: "56vh",
                      overflowY: "auto",
                      paddingRight: 4,
                    }}
                  >
                    {viewModal.note.content || "(No content)"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: 16,
                    }}
                  >
                    <button
                      onClick={() => setViewModal({ open: false, note: null })}
                      style={{
                        padding: "8px 18px",
                        fontSize: 15,
                        background: "#2563eb",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {editModal.open && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.25)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() =>
                  setEditModal({
                    open: false,
                    noteId: null,
                    content: "",
                    topic: "",
                  })
                }
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 4px 32px rgba(0,0,0,0.13)",
                    padding: 32,
                    minWidth: 340,
                    maxWidth: 520,
                    width: "90vw",
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() =>
                      setEditModal({
                        open: false,
                        noteId: null,
                        content: "",
                        topic: "",
                      })
                    }
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 16,
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                  <h2 style={{ marginTop: 0, marginBottom: 18 }}>Edit Note</h2>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!editModal.noteId) return;
                      setSubmitting(true);
                      setError("");
                      try {
                        const res = await fetch(
                          `${apiUrl.replace(/\/$/, "")}/notes/${
                            editModal.noteId
                          }`,
                          {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                            },
                            body: JSON.stringify({
                              content: editModal.content,
                              topic: editModal.topic,
                            }),
                          }
                        );
                        const data = await res.json();
                        if (!res.ok)
                          throw new Error(
                            data.error || "Failed to update note"
                          );
                        setNotes((prev) =>
                          prev.map((n) =>
                            n.noteId === editModal.noteId
                              ? {
                                  ...n,
                                  content: editModal.content,
                                  topic: editModal.topic,
                                }
                              : n
                          )
                        );
                        setEditModal({
                          open: false,
                          noteId: null,
                          content: "",
                          topic: "",
                        });
                        showToast("Note updated!");
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    <input
                      type="text"
                      value={editModal.topic}
                      onChange={(e) =>
                        setEditModal((em) => ({ ...em, topic: e.target.value }))
                      }
                      placeholder="Topic"
                      style={{
                        width: "100%",
                        padding: 10,
                        fontSize: 15,
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        marginBottom: 10,
                      }}
                    />
                    <textarea
                      ref={editContentRef}
                      value={editModal.content}
                      onChange={(e) => {
                        setEditModal((em) => ({
                          ...em,
                          content: e.target.value,
                        }));
                        // Auto-grow as user types
                        const el = editContentRef.current;
                        if (el) {
                          const maxPx = Math.floor(window.innerHeight * 0.5);
                          el.style.height = "auto";
                          el.style.maxHeight = `${maxPx}px`;
                          el.style.overflowY = "auto";
                          el.style.height = `${Math.min(
                            el.scrollHeight,
                            maxPx
                          )}px`;
                        }
                      }}
                      rows={6}
                      style={{
                        width: "100%",
                        padding: 12,
                        fontSize: 16,
                        fontFamily: "inherit",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        boxSizing: "border-box",
                        resize: "none",
                        minHeight: 100,
                        maxHeight: "50vh",
                        overflowY: "auto",
                      }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button
                        type="submit"
                        disabled={submitting}
                        style={{
                          padding: "10px 24px",
                          fontSize: 16,
                          background: "#2563eb",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontWeight: 500,
                        }}
                      >
                        {submitting ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setEditModal({
                            open: false,
                            noteId: null,
                            content: "",
                            topic: "",
                          })
                        }
                        style={{
                          padding: "10px 24px",
                          fontSize: 16,
                          background: "#e5e7eb",
                          color: "#222",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {error && (
                      <div style={{ color: "#b91c1c", marginTop: 10 }}>
                        {error}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}
          </>
        )}
        {tab === "files" && (
          <>
            <h2 style={{ marginBottom: 12 }}>Files</h2>
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                padding: 16,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  type="file"
                  onChange={(e) => setFileToUpload(e.target.files?.[0] || null)}
                  style={{
                    padding: 8,
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
                <select
                  value={filePurpose}
                  onChange={(e) => setFilePurpose(e.target.value)}
                  style={{
                    padding: 8,
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                >
                  <option value="">Purpose (optional)</option>
                  <option value="504">504</option>
                  <option value="IEP">IEP</option>
                  <option value="Evaluation">Evaluation</option>
                  <option value="Other">Other</option>
                </select>
                <button
                  type="button"
                  disabled={uploadBusy || !fileToUpload}
                  onClick={async () => {
                    if (!fileToUpload) return;
                    setUploadBusy(true);
                    setUploadError("");
                    try {
                      setUploadBusy(true);
                      setUploadError(null);

                      // Step 1: Request presigned URL from your backend
                      const res = await fetch(
                        `${apiUrl.replace(/\/$/, "")}/upload-504`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: token ? `Bearer ${token}` : "",
                          },
                          body: JSON.stringify({
                            studentId,
                            fileName: fileToUpload.name,
                            fileType:
                              fileToUpload.type || "application/octet-stream",
                            filePurpose: filePurpose || undefined,
                          }),
                        }
                      );

                      let data;
                      try {
                        data = await res.json();
                      } catch {
                        throw new Error(
                          "Server did not return valid JSON (check backend logs)"
                        );
                      }

                      if (!res.ok)
                        throw new Error(
                          data.error || "Failed to get upload URL"
                        );

                      const { uploadUrl, fileId } = data;

                      // Step 2: Upload file directly to S3
                      // Step 2: Upload file directly to S3
                      try {
                        const s3Response = await fetch(uploadUrl, {
                          method: "PUT",
                          headers: {
                            "Content-Type":
                              fileToUpload.type || "application/octet-stream",
                          },
                          body: fileToUpload,
                        });

                        if (!s3Response.ok) {
                          const text = await s3Response.text().catch(() => "");
                          console.warn(
                            "S3 upload response issue:",
                            s3Response.status,
                            text
                          );
                        }

                        console.log(
                          "✅ File uploaded successfully to S3:",
                          fileId
                        );
                      } catch (err) {
                        // Some browsers block reading the response even when upload succeeds
                        console.warn(
                          "⚠️ Upload likely succeeded but browser blocked response",
                          err
                        );
                      }

                      // Step 3: Refresh file list
                      const ref = await fetch(
                        `${apiUrl.replace(
                          /\/$/,
                          ""
                        )}/students/${studentId}/files`,
                        {
                          headers: {
                            Authorization: token ? `Bearer ${token}` : "",
                          },
                        }
                      );

                      if (ref.ok) {
                        const d2 = await ref.json();
                        setFiles(Array.isArray(d2.files) ? d2.files : []);
                      }

                      setFileToUpload(null);
                      setFilePurpose("");
                      showToast("✅ File uploaded successfully!");
                    } catch (err) {
                      console.error("Upload error:", err);
                      setUploadError(err.message || "Upload failed");
                    } finally {
                      setUploadBusy(false);
                    }
                  }}
                  style={{
                    padding: "8px 14px",
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor:
                      uploadBusy || !fileToUpload ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  {uploadBusy ? "Uploading..." : "Upload"}
                </button>
              </div>
              {uploadError && (
                <div style={{ color: "#b91c1c", marginTop: 8 }}>
                  {uploadError}
                </div>
              )}
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}
            >
              <div
                style={{ padding: 12, background: "#f1f5f9", fontWeight: 600 }}
              >
                Files
              </div>
              {filesLoading ? (
                <div style={{ padding: 16, color: "#64748b" }}>Loading...</div>
              ) : files.length === 0 ? (
                <div style={{ padding: 16, color: "#64748b" }}>
                  No files yet.
                </div>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {files.map((f) => {
                    const id = f.fileId || f.FileId; // tolerate legacy records
                    return (
                      <li
                        key={id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: 12,
                          borderTop: "1px solid #e5e7eb",
                        }}
                      >
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span style={{ fontWeight: 600 }}>
                            {f.fileName || "file"}
                          </span>
                          <span style={{ fontSize: 12, color: "#64748b" }}>
                            {f.filePurpose ? `${f.filePurpose} · ` : ""}
                            {f.fileType || ""}
                            {f.createdAt
                              ? ` · ${new Date(f.createdAt).toLocaleString()}`
                              : ""}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await fetch(
                                  `${apiUrl.replace(
                                    /\/$/,
                                    ""
                                  )}/files/${id}/download`,
                                  {
                                    headers: {
                                      Authorization: token
                                        ? `Bearer ${token}`
                                        : undefined,
                                    },
                                  }
                                );
                                const data = await res.json();
                                if (!res.ok)
                                  throw new Error(
                                    data.error || "Failed to get download URL"
                                  );
                                window.location.href = data.downloadUrl;
                              } catch (e) {
                                setError(e.message);
                              }
                            }}
                            style={{
                              padding: "6px 10px",
                              background: "#e0e7ff",
                              color: "#3730a3",
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  "Are you sure you want to delete this file? This cannot be undone."
                                )
                              )
                                return;
                              try {
                                const res = await fetch(
                                  `${apiUrl.replace(/\/$/, "")}/files/${id}`,
                                  {
                                    method: "DELETE",
                                    headers: {
                                      Authorization: token
                                        ? `Bearer ${token}`
                                        : undefined,
                                    },
                                  }
                                );
                                const data = await res.json();
                                if (!res.ok)
                                  throw new Error(
                                    data.error || "Failed to delete file"
                                  );
                                // Refresh list
                                const ref = await fetch(
                                  `${apiUrl.replace(
                                    /\/$/,
                                    ""
                                  )}/students/${studentId}/files`,
                                  {
                                    headers: {
                                      Authorization: token
                                        ? `Bearer ${token}`
                                        : undefined,
                                    },
                                  }
                                );
                                if (ref.ok) {
                                  const d2 = await ref.json();
                                  setFiles(
                                    Array.isArray(d2.files) ? d2.files : []
                                  );
                                }
                                showToast("File deleted");
                              } catch (e) {
                                setError(e.message);
                              }
                            }}
                            style={{
                              padding: "6px 10px",
                              background: "#fee2e2",
                              color: "#991b1b",
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
        {tab === "meetings" && (
          <>
            <h2 style={{ marginBottom: 12 }}>Recent Meetings</h2>
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                padding: 16,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  style={{
                    padding: "8px 10px",
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
                <button
                  type="button"
                  disabled={meetingBusy || !meetingDate}
                  onClick={async () => {
                    if (!meetingDate) return;
                    // Parse input (support yyyy-mm-dd or mm/dd/yyyy)
                    let d = new Date(meetingDate);
                    if (isNaN(d.getTime())) {
                      const dash = meetingDate.split("-");
                      const slash = meetingDate.split("/");
                      if (dash.length === 3) {
                        d = new Date(
                          Number(dash[0]),
                          Number(dash[1]) - 1,
                          Number(dash[2])
                        );
                      } else if (slash.length === 3) {
                        // interpret as MM/DD/YYYY
                        d = new Date(
                          Number(slash[2]),
                          Number(slash[0]) - 1,
                          Number(slash[1])
                        );
                      }
                    }
                    if (isNaN(d.getTime())) return;
                    const iso = d.toISOString();
                    const current = Array.isArray(student?.meetings)
                      ? student.meetings
                      : [];
                    const next = Array.from(new Set([iso, ...current]));
                    setMeetingBusy(true);
                    setMeetingError("");
                    try {
                      const res = await fetch(
                        `${apiUrl.replace(/\/$/, "")}/students/${studentId}`,
                        {
                          method: "PUT",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: token
                              ? `Bearer ${token}`
                              : undefined,
                          },
                          body: JSON.stringify({ meetings: next }),
                        }
                      );
                      const data = await res.json();
                      if (!res.ok)
                        throw new Error(data.error || "Failed to add meeting");
                      setStudent((prev) => ({ ...prev, ...data.student }));
                      setMeetingDate("");
                      showToast("Meeting added");
                    } catch (err) {
                      setMeetingError(err.message);
                    } finally {
                      setMeetingBusy(false);
                    }
                  }}
                  style={{
                    padding: "8px 14px",
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor:
                      meetingBusy || !meetingDate ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  {meetingBusy ? "Saving..." : "Add Meeting"}
                </button>
                <button
                  type="button"
                  disabled={meetingBusy}
                  onClick={async () => {
                    const now = new Date();
                    // Zero out time to treat as date-only
                    const d = new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate()
                    );
                    const iso = d.toISOString();
                    const current = Array.isArray(student?.meetings)
                      ? student.meetings
                      : [];
                    const next = Array.from(new Set([iso, ...current]));
                    setMeetingBusy(true);
                    setMeetingError("");
                    try {
                      const res = await fetch(
                        `${apiUrl.replace(/\/$/, "")}/students/${studentId}`,
                        {
                          method: "PUT",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: token
                              ? `Bearer ${token}`
                              : undefined,
                          },
                          body: JSON.stringify({ meetings: next }),
                        }
                      );
                      const data = await res.json();
                      if (!res.ok)
                        throw new Error(data.error || "Failed to add meeting");
                      setStudent((prev) => ({ ...prev, ...data.student }));
                      showToast("Meeting added");
                    } catch (err) {
                      setMeetingError(err.message);
                    } finally {
                      setMeetingBusy(false);
                    }
                  }}
                  style={{
                    padding: "8px 14px",
                    background: "#e0e7ff",
                    color: "#3730a3",
                    border: "none",
                    borderRadius: 6,
                    cursor: meetingBusy ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Today
                </button>
              </div>
              {meetingError && (
                <div style={{ color: "#b91c1c", marginTop: 8 }}>
                  {meetingError}
                </div>
              )}
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}
            >
              <div
                style={{ padding: 12, background: "#f1f5f9", fontWeight: 600 }}
              >
                Meetings
              </div>
              {(!student.meetings || student.meetings.length === 0) && (
                <div style={{ padding: 16, color: "#64748b" }}>
                  No meetings recorded yet.
                </div>
              )}
              {Array.isArray(student.meetings) &&
                student.meetings.length > 0 && (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {student.meetings
                      .slice()
                      .sort(
                        (a, b) => new Date(b).getTime() - new Date(a).getTime()
                      )
                      .map((mIso) => (
                        <li
                          key={mIso}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 12,
                            borderTop: "1px solid #e5e7eb",
                          }}
                        >
                          <span>{new Date(mIso).toLocaleDateString()}</span>
                          <button
                            type="button"
                            disabled={meetingBusy}
                            onClick={async () => {
                              const current = Array.isArray(student?.meetings)
                                ? student.meetings
                                : [];
                              const next = current.filter((x) => x !== mIso);
                              setMeetingBusy(true);
                              setMeetingError("");
                              try {
                                const res = await fetch(
                                  `${apiUrl.replace(
                                    /\/$/,
                                    ""
                                  )}/students/${studentId}`,
                                  {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                      Authorization: token
                                        ? `Bearer ${token}`
                                        : undefined,
                                    },
                                    body: JSON.stringify({ meetings: next }),
                                  }
                                );
                                const data = await res.json();
                                if (!res.ok)
                                  throw new Error(
                                    data.error || "Failed to remove meeting"
                                  );
                                setStudent((prev) => ({
                                  ...prev,
                                  ...data.student,
                                }));
                                showToast("Meeting removed");
                              } catch (err) {
                                setMeetingError(err.message);
                              } finally {
                                setMeetingBusy(false);
                              }
                            }}
                            style={{
                              padding: "6px 10px",
                              background: "#fee2e2",
                              color: "#991b1b",
                              border: "none",
                              borderRadius: 6,
                              cursor: meetingBusy ? "not-allowed" : "pointer",
                              fontWeight: 600,
                            }}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentDetail;
